import React from 'react';
import { api } from '../api.js';
import { loadAll } from '../load.js';
import { Avatar, Empty, Icon, Loading, Modal, PH, PageTabs, Panel } from './components.jsx';
import { Button, Checkbox, DataTable, Field, Pill } from './ds/fgcp-ds.jsx';
import { OPS_KIND, opsPeriod } from './ops.js';
import { MailAgentAdmin } from './mail-agent-admin.jsx';
import { PrincipalsAdmin } from './principals-admin.jsx';
import { ServiceCallsModal } from './service-calls-admin.jsx';
import { PEOPLE } from './data.jsx';
import * as T from '../time.js';

// admin.jsx — 관리자: 조직도 편집 · 권한 · Entra 반영 (설계 §1)
// 기준은 Workspace. 저장하면 DB에 확정되고, Entra에는 뒤에서 반영된다(실패 시 재시도).
const { useState, useEffect, useMemo } = React;

const selStyle = {
  fontFamily: 'var(--font-ui)', fontSize: 'var(--fs-body)', padding: '9px 11px',
  border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)',
  color: 'var(--ink)', width: '100%', boxSizing: 'border-box',
};
function Sel({ label, value, onChange, options, placeholder }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' }}>{label}</span>}
      <select style={selStyle} value={value ?? ''} onChange={e => onChange(e.target.value || null)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </label>
  );
}

/** 부서 트리를 깊이 우선으로 펼친 목록 + 경로 이름 */
function useTree(depts) {
  return useMemo(() => {
    const kids = id => depts.filter(d => d.parent_id === id).sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
    const root = depts.find(d => !d.parent_id);
    const flat = [];
    const walk = (d, depth, path) => { flat.push({ ...d, depth, path }); kids(d.id).forEach(c => walk(c, depth + 1, path ? `${path} / ${c.name}` : c.name)); };
    if (root) walk(root, 0, '');
    const subtree = id => [id, ...kids(id).flatMap(c => subtree(c.id))];
    return { root, flat, subtree, byId: Object.fromEntries(depts.map(d => [d.id, d])) };
  }, [depts]);
}

function Admin({ onToast }) {
  const [tab, setTab] = useState('org');
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const reload = async () => {
    try { setData(await api.get('/api/v1/admin/org')); setErr(''); }
    catch (e) { setErr(e.message); }
  };
  useEffect(() => { reload(); }, []);
  /** 저장 공통: 실패하면 서버 메시지를 그대로 보여준다 */
  const run = async (fn, okMsg) => {
    try { await fn(); await reload(); loadAll().catch(() => {}); if (okMsg) onToast(okMsg); return true; }
    catch (e) { onToast(e.message); return false; }
  };

  if (err) return <div className="body"><Empty icon="shield" title="관리자 화면을 열 수 없습니다" sub={err} /></div>;
  if (!data) return <div className="body"><Loading title="불러오는 중" /></div>;
  const pending = data.users.filter(u => u.entra_push_pending || u.entra_push_error).length;

  return (
    <>
      <div className="head">
        <h1>관리자</h1>
      </div>
      <PageTabs value={tab} onChange={setTab} items={[
        { value: 'org', label: '조직도' }, { value: 'lists', label: '직급·고용 형태' }, { value: 'roles', label: '권한' }, { value: 'entra', label: 'Entra 반영', count: pending || undefined },
        { value: 'outbox', label: '알림 발송' }, { value: 'ops', label: '운영 공지' }, { value: 'services', label: '서비스' }, { value: 'mailagent', label: '메일 에이전트' }, { value: 'principals', label: '검색 권한' }, { value: 'audit', label: '감사' }]} />
      <div className="body" style={{ paddingTop: 14 }}>
        {tab === 'org' && <OrgAdmin data={data} run={run} />}
        {tab === 'lists' && <ListsAdmin data={data} run={run} />}
        {tab === 'mailagent' && <MailAgentAdmin onToast={onToast} />}
        {tab === 'roles' && <RoleAdmin data={data} run={run} />}
        {tab === 'entra' && <EntraAdmin data={data} run={run} onToast={onToast} />}
        {tab === 'outbox' && <OutboxAdmin onToast={onToast} />}
        {tab === 'ops' && <OpsAdmin onToast={onToast} />}
        {tab === 'services' && <ServiceAdmin onToast={onToast} />}
        {tab === 'principals' && <PrincipalsAdmin onToast={onToast} />}
        {tab === 'audit' && <AuditAdmin onToast={onToast} />}
      </div>
    </>
  );
}

// ─── 조직도 ───────────────────────────────────────────────
const UNASSIGNED = '__unassigned';

function OrgAdmin({ data, run }) {
  const { root, flat, subtree, byId } = useTree(data.departments);
  const [sel, setSel] = useState(flat[1]?.id ?? root?.id);
  const [edit, setEdit] = useState(null);       // 사람 수정 모달
  const [newDept, setNewDept] = useState(null); // 부서 추가 모달
  const active = data.users.filter(u => u.employment_status === 'active');
  const unassigned = data.users.filter(u => !u.department_id && u.entra_account_enabled && u.in_directory);
  const countOf = id => { const ids = new Set(subtree(id)); return active.filter(u => ids.has(u.department_id)).length; };

  const dept = sel === UNASSIGNED ? null : byId[sel];
  const members = sel === UNASSIGNED ? unassigned : data.users.filter(u => u.department_id === sel);

  return (
    <div className="row2" style={{ gridTemplateColumns: '260px 1fr' }}>
      <Panel>
        <PH title="부서" sub={`${flat.length - 1}개`} right={root && <Button size="sm" variant="secondary" onClick={() => setNewDept({ parentId: sel && sel !== UNASSIGNED ? sel : root.id })}>+ 부서</Button>} />
        <div style={{ padding: 8 }}>
          {flat.map(d => (
            <button key={d.id} onClick={() => setSel(d.id)} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
              padding: '7px 9px', paddingLeft: 9 + d.depth * 14, borderRadius: 'var(--r-sm)', fontSize: 12.5,
              fontWeight: sel === d.id ? 600 : d.depth === 0 ? 700 : 500,
              color: sel === d.id ? 'var(--acc)' : 'var(--ink-2)', background: sel === d.id ? 'var(--acc-tint)' : 'transparent' }}>
              {d.depth === 0 && <Icon name="building" size={14} color="var(--ink-3)" />}
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name}</span>
              <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)' }}>{countOf(d.id)}</span>
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--line)', margin: '8px 0' }} />
          <button onClick={() => setSel(UNASSIGNED)} style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', padding: '7px 9px',
            borderRadius: 'var(--r-sm)', fontSize: 12.5, fontWeight: sel === UNASSIGNED ? 600 : 500,
            color: sel === UNASSIGNED ? 'var(--acc)' : 'var(--ink-2)', background: sel === UNASSIGNED ? 'var(--acc-tint)' : 'transparent' }}>
            <Icon name="user-x" size={14} color="var(--ink-3)" />부서 미배정
            <span className="mono" style={{ marginLeft: 'auto', fontSize: 10.5, color: 'var(--ink-3)' }}>{unassigned.length}</span>
          </button>
        </div>
      </Panel>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        {dept && <DeptForm key={dept.id} dept={dept} flat={flat} subtree={subtree} users={active} run={run} onDeleted={() => setSel(root.id)} />}
        {sel === UNASSIGNED && (
          <Panel><div style={{ padding: '12px 16px', fontSize: 12.5, color: 'var(--ink-2)' }}>
            부서가 없는 계정은 Workspace를 사용할 수 없습니다(Entra 계정은 그대로). 부서를 지정하면 바로 사용할 수 있게 됩니다.
          </div></Panel>
        )}
        <Panel scroll>
          <PH title={sel === UNASSIGNED ? '부서 미배정' : '구성원'} sub={`${members.length}명 · 직속`} />
          {members.length === 0 ? <Empty icon="users" title="구성원이 없습니다" /> :
            <DataTable columns={[
              { key: 'display_name', header: '이름', strong: true, render: u => (
                <button onClick={() => setEdit(u)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, color: 'var(--ink)' }}>
                  <Avatar name={u.display_name} size={22} />{u.display_name}
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 400 }}>{u.title || ''}</span>
                </button>) },
              { key: 'email', header: '이메일', render: u => <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{u.email}</span> },
              { key: 'mobile_phone', header: '휴대폰', mono: true, width: 132, render: u => <span style={{ whiteSpace: 'nowrap' }}>{u.mobile_phone || '-'}</span> },
              { key: 'st', header: '상태', width: 110, render: u => <UserState u={u} /> },
            ]} rows={members.map(u => ({ ...u, id: u.id }))} footer={<span>이름을 누르면 부서·직급·고용 형태·입퇴사일·연락처를 바꿀 수 있습니다</span>} />}
        </Panel>
      </div>

      {edit && <UserModal user={edit} flat={flat} lists={data} onClose={() => setEdit(null)}
        onSave={async body => { if (await run(() => api.patch(`/api/v1/admin/users/${edit.id}`, body), '저장했습니다. Entra 반영 중')) setEdit(null); }} />}
      {newDept && <NewDeptModal init={newDept} flat={flat} onClose={() => setNewDept(null)}
        onSave={async body => { if (await run(() => api.post('/api/v1/admin/departments', body), '부서를 만들었습니다')) setNewDept(null); }} />}
    </div>
  );
}

function UserState({ u }) {
  if (!u.entra_account_enabled) return <Pill tone="neutral" dot>Entra 중지</Pill>;
  if (u.employment_status !== 'active') return <Pill tone="warn" dot>사용 불가</Pill>;
  if (u.entra_push_error) return <Pill tone="crit" dot>반영 오류</Pill>;
  if (u.entra_push_pending) return <Pill tone="info" dot>반영 대기</Pill>;
  return <Pill tone="ok" dot>사용 중</Pill>;
}

function DeptForm({ dept, flat, subtree, users, run, onDeleted }) {
  const [name, setName] = useState(dept.name);
  const [parentId, setParentId] = useState(dept.parent_id);
  const [managerId, setManagerId] = useState(dept.manager_user_id);
  const [code, setCode] = useState(dept.code);
  const isRoot = !dept.parent_id;
  const blocked = new Set(subtree(dept.id));   // 자기와 하위로는 옮길 수 없다
  const changed = name !== dept.name || parentId !== dept.parent_id || managerId !== dept.manager_user_id || code !== dept.code;
  const save = () => run(() => api.patch(`/api/v1/admin/departments/${dept.id}`, {
    name, code, managerUserId: managerId, ...(isRoot ? {} : { parentId }) }), '부서를 저장했습니다');
  const del = async () => {
    if (!window.confirm(`'${dept.name}' 부서를 삭제할까요?`)) return;
    if (await run(() => api.del(`/api/v1/admin/departments/${dept.id}`), '부서를 삭제했습니다')) onDeleted();
  };
  return (
    <Panel>
      <PH title={dept.name} sub={isRoot ? '회사' : dept.code} right={<div style={{ display: 'flex', gap: 6 }}>
        {!isRoot && <Button size="sm" variant="secondary" onClick={del}>삭제</Button>}
        <Button size="sm" disabled={!changed || !name.trim()} onClick={save}>저장</Button></div>} />
      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <Field label="부서 이름" value={name} onChange={e => setName(e.target.value)} />
        <Field label="코드" value={code} onChange={e => setCode(e.target.value)} />
        {!isRoot && <Sel label="상위 부서" value={parentId} onChange={setParentId}
          options={flat.filter(d => !blocked.has(d.id)).map(d => ({ v: d.id, l: d.path || d.name }))} />}
        <Sel label="부서장" value={managerId} onChange={setManagerId} placeholder="— 없음 —"
          options={users.map(u => ({ v: u.id, l: `${u.display_name}${u.title ? ' ' + u.title : ''}` }))} />
      </div>
    </Panel>
  );
}

/** 사람의 인사 기록 — 직급·고용 형태는 사용 중인 목록에서 고른다(목록 밖의 지난 직급은 그대로 두거나 목록 값으로 바꾼다). 검사는 서버 */
function UserModal({ user, flat, lists, onClose, onSave }) {
  const [departmentId, setDept] = useState(user.department_id);
  const [title, setTitle] = useState(user.title ?? null);
  const [employmentTypeId, setType] = useState(user.employment_type_id ?? null);
  const [joinedAt, setJoined] = useState(user.joined_at ?? '');
  const [leftAt, setLeft] = useState(user.left_at ?? '');
  const [mobilePhone, setMobile] = useState(user.mobile_phone ?? '');
  const [businessPhone, setBiz] = useState(user.business_phone ?? '');
  const titles = lists.jobTitles.filter(t => t.active).map(t => ({ v: t.name, l: t.name }));
  if (user.title && !titles.some(t => t.v === user.title)) titles.unshift({ v: user.title, l: `${user.title} (목록에 없음 — 지금 값)` });
  const types = lists.employmentTypes.filter(t => t.active || t.id === user.employment_type_id).map(t => ({ v: t.id, l: t.active ? t.name : `${t.name} (사용 중지)` }));
  return (
    <Modal width={460} title={`${user.display_name} — 사원 정보`} icon="user" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" onClick={() => onSave({ departmentId, title, employmentTypeId, joinedAt: joinedAt || null, leftAt: leftAt || null, mobilePhone, businessPhone })}>저장</Button></>}>
      <div data-user-edit={user.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{user.email}</div>
        <Sel label="부서" value={departmentId} onChange={setDept} placeholder="— 미배정 (Workspace 사용 불가) —"
          options={flat.map(d => ({ v: d.id, l: d.path || d.name }))} />
        <Sel label="직급" value={title} onChange={setTitle} placeholder="— 없음 —" options={titles} />
        <Sel label="고용 형태" value={employmentTypeId} onChange={setType} placeholder="— 없음 —" options={types} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="입사일" type="date" value={joinedAt} onChange={e => setJoined(e.target.value)} />
          <Field label="퇴사일" type="date" value={leftAt} onChange={e => setLeft(e.target.value)} />
        </div>
        <Field label="휴대폰" value={mobilePhone} onChange={e => setMobile(e.target.value)} placeholder="010-0000-0000" />
        <Field label="사무실 전화" value={businessPhone} onChange={e => setBiz(e.target.value)} />
        {user.entra_push_error && <div style={{ fontSize: 11.5, color: 'var(--crit)' }}>최근 Entra 반영: {user.entra_push_error}</div>}
      </div>
    </Modal>
  );
}

// ─── 직급·고용 형태 목록 ───────────────────────────────────
function ListsAdmin({ data, run }) {
  return (
    <div className="row2" style={{ gridTemplateColumns: '1fr 1fr' }}>
      <ListEditor key={'jt' + JSON.stringify(data.jobTitles)} kind="job-titles" title="직급" items={data.jobTitles} run={run} />
      <ListEditor key={'et' + JSON.stringify(data.employmentTypes)} kind="employment-types" title="고용 형태" items={data.employmentTypes} run={run} />
    </div>
  );
}

/** 목록 편집 — 저장하면 보이는 순서·이름·사용 여부가 새 전체가 된다. 쓰는 사람이 있는 항목은 빼지 못한다(서버 409 — 사용 중지로) */
function ListEditor({ kind, title, items, run }) {
  const [rows, setRows] = useState(items.map(i => ({ id: i.id, name: i.name, active: i.active, inUse: i.in_use })));
  const set = (n, patch) => setRows(rows.map((r, k) => (k === n ? { ...r, ...patch } : r)));
  const move = (n, d) => { const k = n + d; if (k < 0 || k >= rows.length) return; const next = [...rows]; [next[n], next[k]] = [next[k], next[n]]; setRows(next); };
  const save = () => run(() => api.put(`/api/v1/admin/${kind}`, { items: rows.map(r => ({ ...(r.id ? { id: r.id } : {}), name: r.name.trim(), active: r.active })) }), `${title} 목록을 저장했습니다`);
  return (
    <Panel scroll>
      <PH title={title} sub={`${rows.length}개`} right={<div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" variant="secondary" onClick={() => setRows([...rows, { name: '', active: true, inUse: 0 }])}>+ 추가</Button>
        <Button size="sm" disabled={rows.some(r => !r.name.trim())} onClick={save}>저장</Button></div>} />
      <div data-list={kind} style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {rows.map((r, n) => (
          <div key={r.id ?? 'new' + n} data-item={r.name} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto auto', gap: 8, alignItems: 'center' }}>
            <Field aria-label={`${title} 이름`} value={r.name} onChange={e => set(n, { name: e.target.value })} maxLength={30} />
            <Checkbox size="sm" label="사용" checked={r.active} onChange={v => set(n, { active: v })} style={{ whiteSpace: 'nowrap' }} />
            <span className="mono" title="이 항목을 쓰는 사람" style={{ fontSize: 11, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{r.inUse}명</span>
            <div style={{ display: 'flex', gap: 2 }}>
              <Button size="sm" variant="ghost" aria-label="위로" onClick={() => move(n, -1)}>↑</Button>
              <Button size="sm" variant="ghost" aria-label="아래로" onClick={() => move(n, 1)}>↓</Button>
            </div>
            <Button size="sm" variant="ghost" aria-label={`${r.name || '새 항목'} 빼기`} disabled={r.inUse > 0} onClick={() => setRows(rows.filter((_, k) => k !== n))}>빼기</Button>
          </div>
        ))}
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>쓰는 사람이 있는 항목은 뺄 수 없고 사용을 끄면 새로 고를 수 없습니다. 직급 이름을 바꾸면 그 직급인 사람의 직급도 바뀝니다(Entra 반영).</div>
      </div>
    </Panel>
  );
}

function NewDeptModal({ init, flat, onClose, onSave }) {
  const [parentId, setParentId] = useState(init.parentId);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  return (
    <Modal width={420} title="부서 추가" icon="building" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" disabled={!name.trim()} onClick={() => onSave({ parentId, name, code })}>추가</Button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Sel label="상위 부서" value={parentId} onChange={setParentId} options={flat.map(d => ({ v: d.id, l: d.path || d.name }))} />
        <Field label="부서 이름" value={name} onChange={e => setName(e.target.value)} placeholder="예: Cloud 3팀" />
        <Field label="코드 (비우면 자동)" value={code} onChange={e => setCode(e.target.value)} />
      </div>
    </Modal>
  );
}

// ─── 권한 ─────────────────────────────────────────────────
function RoleAdmin({ data, run }) {
  const { flat, byId } = useTree(data.departments);
  const [role, setRole] = useState(data.roles.find(r => r.code !== 'user')?.code);
  const [addUser, setAddUser] = useState(null);
  const [addDept, setAddDept] = useState(null);
  const [inc, setInc] = useState(true);
  const r = data.roles.find(x => x.code === role);
  // 범위 역할(부서 관리자)은 맡은 부서와 함께 — 부서 범위 부여만 센다(전역 부여는 권한이 없다, 계획 문서 5.4 4b)
  const people = r?.scoped
    ? data.users.flatMap(u => (u.scoped_roles || []).filter(x => x.code === role).map(x => ({ ...u, scopeDept: x.departmentId })))
    : data.users.filter(u => u.roles.includes(role));
  const [meta, setMeta] = useState(null);   // 이름·설명 고치기
  const depts = data.departments.filter(d => d.roles.some(x => x.code === role));
  const activeUsers = data.users.filter(u => u.employment_status === 'active');

  return (
    <div className="row2" style={{ gridTemplateColumns: '260px 1fr' }}>
      <Panel>
        <PH title="역할" sub={`${data.roles.length}개`} />
        <div style={{ padding: 8 }}>
          {data.roles.map(x => (
            <button key={x.code} onClick={() => setRole(x.code)} style={{
              display: 'block', width: '100%', textAlign: 'left', padding: '8px 9px', borderRadius: 'var(--r-sm)',
              background: role === x.code ? 'var(--acc-tint)' : 'transparent' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: role === x.code ? 'var(--acc)' : 'var(--ink)' }}>{x.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{x.description || x.code}</div>
            </button>
          ))}
        </div>
      </Panel>
      {r && <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
        <Panel>
          <PH title={r.name} sub={r.code} right={r.editable && <Button size="sm" variant="secondary" onClick={() => setMeta({ name: r.name, description: r.description ?? '' })}>이름·설명</Button>} />
          <div data-role-grants={r.code} style={{ padding: '10px 16px', fontSize: 12.5, color: 'var(--ink-2)' }}>{r.grants || '—'}</div>
          {meta && <div style={{ padding: '0 16px 12px', display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'end' }}>
            <Field label="이름" value={meta.name} maxLength={40} onChange={e => setMeta({ ...meta, name: e.target.value })} />
            <Field label="설명" value={meta.description} maxLength={300} onChange={e => setMeta({ ...meta, description: e.target.value })} />
            <Button size="sm" disabled={!meta.name.trim()} onClick={async () => {
              if (await run(() => api.patch(`/api/v1/admin/roles/${encodeURIComponent(r.code)}`, { name: meta.name, description: meta.description || null }), '역할 이름·설명을 저장했습니다')) setMeta(null); }}>저장</Button>
          </div>}
        </Panel>
        <Panel>
          <PH title={`${r.name} — 사람`} sub={`${people.length}명`} />
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {people.map(u => (
              <div key={u.id + (u.scopeDept || '')} data-role-holder={u.email} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <Avatar name={u.display_name} size={22} />{u.display_name}
                {u.scopeDept && <Pill tone="info" dot={false}>{flat.find(f => f.id === u.scopeDept)?.path || byId[u.scopeDept]?.name}</Pill>}
                <span style={{ color: 'var(--ink-3)', fontSize: 11 }}>{u.email}</span>
                <Button size="sm" variant="secondary" style={{ marginLeft: 'auto' }}
                  onClick={() => run(() => api.post(`/api/v1/admin/users/${u.id}/roles`, { revoke: [role], ...(u.scopeDept ? { departmentId: u.scopeDept } : {}) }), '회수했습니다')}>회수</Button>
              </div>))}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}><Sel value={addUser} onChange={setAddUser} placeholder="— 사람 선택 —"
                options={activeUsers.filter(u => r.scoped || !u.roles.includes(role)).map(u => ({ v: u.id, l: `${u.display_name} (${u.email})` }))} /></div>
              {r.scoped && <div style={{ flex: 1 }}><Sel value={addDept} onChange={setAddDept} placeholder="— 맡을 부서 —"
                options={flat.map(d => ({ v: d.id, l: d.path || d.name }))} /></div>}
              <Button size="sm" disabled={!addUser || (r.scoped && !addDept)} onClick={async () => {
                if (await run(() => api.post(`/api/v1/admin/users/${addUser}/roles`, { grant: [role], ...(r.scoped ? { departmentId: addDept } : {}) }), '부여했습니다')) { setAddUser(null); setAddDept(null); } }}>부여</Button>
            </div>
            {r.scoped && <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>부서장은 따로 주지 않아도 그 부서의 부서 관리자입니다. 여기서는 부서장 외에 그 부서(하위 포함)를 맡을 사람을 정합니다.</div>}
          </div>
        </Panel>
        {role !== 'system_admin' && !r.scoped && <Panel>
          <PH title={`${r.name} — 부서`} sub={`${depts.length}개`} />
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {depts.map(d => {
              const g = d.roles.find(x => x.code === role);
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <Icon name="building" size={14} color="var(--ink-3)" />{flat.find(f => f.id === d.id)?.path || byId[d.id]?.name}
                  <Pill tone="neutral" dot={false}>{g.includeDescendants ? '하위 포함' : '이 부서만'}</Pill>
                  <Button size="sm" variant="secondary" style={{ marginLeft: 'auto' }}
                    onClick={() => run(() => api.post(`/api/v1/admin/departments/${d.id}/roles`, { revoke: [role] }), '회수했습니다')}>회수</Button>
                </div>);
            })}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}><Sel value={addDept} onChange={setAddDept} placeholder="— 부서 선택 —"
                options={flat.map(d => ({ v: d.id, l: d.path || d.name }))} /></div>
              <Checkbox size="sm" label="하위 포함" checked={inc} onChange={setInc} style={{ whiteSpace: 'nowrap', paddingBottom: 9 }} />
              <Button size="sm" disabled={!addDept} onClick={async () => {
                if (await run(() => api.post(`/api/v1/admin/departments/${addDept}/roles`, { grant: [role], includeDescendants: inc }), '부여했습니다')) setAddDept(null); }}>부여</Button>
            </div>
          </div>
        </Panel>}
      </div>}
    </div>
  );
}

// ─── Entra 반영 ───────────────────────────────────────────
// 반영·동기화는 프로비저너가 플랫폼 앱 자격으로 한다(설계 결정 9) — 이 화면은 상태를 보고 요청만 남긴다
function EntraAdmin({ data, run, onToast }) {
  const rows = data.users.filter(u => u.entra_push_pending || u.entra_push_error);
  const waiting = rows.filter(u => u.entra_push_next_at && new Date(u.entra_push_next_at) > new Date()).length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <PlatformEntraPanel onToast={onToast} />
      <Panel scroll>
        <PH title="Entra 반영 상태" sub="Workspace에서 바꾼 부서·직급·연락처 — 1분 안에 자동 반영, 실패하면 1·2·4…분 뒤 다시 시도(최대 1시간 간격)" right={
          waiting > 0 && <Button size="sm" onClick={() => run(() => api.post('/api/v1/admin/entra/push'), '다음 실행(1분 안)에 다시 시도합니다')}>실패 항목 지금 다시 시도</Button>} />
        {rows.length === 0 ? <Empty icon="check-circle" title="모두 반영되었습니다" sub="바꾼 값은 1분 안에 Entra에 반영됩니다" /> :
          <DataTable columns={[
            { key: 'display_name', header: '이름', strong: true },
            { key: 'email', header: '이메일' },
            { key: 'st', header: '상태', width: 100, render: u => <UserState u={u} /> },
            { key: 'entra_push_error', header: '내용', render: u => <span style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>{u.entra_push_error || '대기 중'}
              {u.entra_push_pending && u.entra_push_next_at && <span style={{ color: 'var(--ink-3)' }}> · 다음 시도 {T.hm(u.entra_push_next_at)}({u.entra_push_attempts}회 실패)</span>}</span> },
          ]} rows={rows.map(u => ({ ...u, id: u.id }))} />}
      </Panel>
    </div>
  );
}

const PF_LEVEL = { ok: ['정상', 'ok'], limited: ['일부 권한 없음', 'warn'], not_consented: ['동의 필요', 'crit'], error: ['오류', 'crit'], not_configured: ['설정 전', 'warn'] };
const CERT_TONE = { ok: 'ok', warn: 'warn', critical: 'crit', unknown: 'neutral' };
const fmtDay = (v) => v ? T.ymd(v) : '—';

function PlatformEntraPanel({ onToast }) {
  const [d, setD] = useState(null);
  const load = () => api.get('/api/v1/admin/platform/entra').then(setD).catch(e => onToast(e.message));
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);
  const p = d?.platform, ds = d?.directorySync, cw = d?.certWatch || {};
  const [l, t] = PF_LEVEL[p?.level] ?? ['확인 전', 'neutral'];
  const requested = d?.directoryRequest && (!ds || new Date(d.directoryRequest.requestedAt) > new Date(ds.lastRunAt));
  const sync = () => api.post('/api/v1/admin/directory/sync').then(() => { onToast('다음 실행(1분 안)에 동기화합니다'); load(); }).catch(e => onToast(e.message));
  return (
    <Panel>
      <PH title="플랫폼 Entra 앱" sub="서비스 앱 관리·디렉터리 동기화·조직도 반영은 프로비저너가 플랫폼 앱(FGCP Workspace Platform)으로 합니다 · 인터넷에 열린 Workspace는 이 권한이 없습니다"
        right={<Pill tone={t} dot>{l}</Pill>} />
      {!d ? <Loading title="불러오는 중" /> : <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '0 2px', fontSize: 12, color: 'var(--ink-2)' }}>
        {p?.level === 'not_consented' && <div style={{ color: 'var(--crit)' }}>권한 동의가 없습니다({(p.missingRoles || []).join(', ')}) — Entra 관리 센터에서 플랫폼 앱의 'API 권한 → 관리자 동의'가 필요합니다.</div>}
        {p?.level === 'limited' && <div style={{ color: 'var(--warn)' }}>전화번호를 Entra에 반영할 권한({(p.missingRoles || []).join(', ')})이 없습니다 — 부서·직급은 반영되고, 전화번호는 플랫폼 앱의 'API 권한 → 관리자 동의' 뒤 자동으로 반영됩니다.</div>}
        {p?.level === 'error' && <div style={{ color: 'var(--crit)' }}>{p.error}</div>}
        {p?.level === 'not_configured' && <div style={{ color: 'var(--warn)' }}>플랫폼 앱 자격이 아직 없습니다 — 서비스 등록·비밀키 교체·디렉터리 동기화·조직도 반영이 기다립니다.</div>}
        <div>인증서 만료 — 플랫폼 앱 <Pill tone={CERT_TONE[cw.platform] ?? 'neutral'} dot={false}>{fmtDay(p?.certNotAfter)}</Pill>
          {' '}· 로그인 앱 <Pill tone={CERT_TONE[cw.signin] ?? 'neutral'} dot={false}>{d.signInCert?.secretOnly ? '비밀키 사용' : fmtDay(d.signInCert?.notAfter)}</Pill>
          {cw.botAppId && <>{' '}· Teams Bot 비밀키 <Pill tone={CERT_TONE[cw.bot] ?? 'neutral'} dot={false}>{cw.botSecrets === 0 ? '없음' : cw.botNotAfter ? fmtDay(cw.botNotAfter) : '확인 못 함'}</Pill></>}
          <span style={{ color: 'var(--ink-3)' }}> · 30일 전 확인 필요, 7일 전 긴급 알림</span></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>디렉터리 동기화(1시간마다) — {ds ? <>{T.dateTime(ds.lastRunAt)} {ds.ok ? <Pill tone="ok" dot>성공</Pill> : <Pill tone="crit" dot>실패</Pill>}
            {ds.ok && ds.result && <span style={{ color: 'var(--ink-3)' }}> · {ds.result.fetched}명 확인, 신규 {ds.result.created}, 비활성 {ds.result.deactivated}{ds.result.unknownDepartments?.length ? `, 조직도에 없는 부서 경로 ${ds.result.unknownDepartments.length}` : ''}</span>}
            {!ds.ok && <span style={{ color: 'var(--crit)' }}> · {ds.error}</span>}</> : '아직 없음'}</span>
          <Button size="sm" variant="secondary" disabled={Boolean(requested) || !['ok', 'limited'].includes(p?.level)} onClick={sync}>{requested ? '요청됨' : '지금 동기화'}</Button>
        </div>
        {(d.retiredApps || []).length > 0 && <div style={{ color: 'var(--warn)' }}>정리 대기 중인 이전 Entra 앱 {d.retiredApps.length}개: {d.retiredApps.map(r => `${r.display_name || r.app_id}${r.slug ? `(${r.slug})` : ''}`).join(', ')}
          <span style={{ color: 'var(--ink-3)' }}> — 지울 때까지는 이전 앱으로 온 토큰도 받습니다</span></div>}
      </div>}
    </Panel>
  );
}

// ─── 알림 발송 ────────────────────────────────────────────
// calendar.sync = Outlook 일정을 대상의 지금 상태로 맞추기(2-3c). calendar.create·cancel은 그 전 버전이 남긴 기록
const KIND = { mail: '메일', teams: 'Teams', teams_feed: 'Teams 활동', 'calendar.sync': 'Outlook 일정', 'calendar.create': 'Outlook 초대', 'calendar.cancel': 'Outlook 취소' };
const OST = { sent: ['보냄', 'ok'], pending: ['대기', 'info'], skipped: ['건너뜀', 'neutral'], failed: ['실패', 'crit'] };

/** Teams 조직 카탈로그 앱 id — 알림 카드의 '열기'를 Teams 안 Workspace 탭 그 화면으로(없으면 웹 링크) */
function TeamsCatalogId({ value, onSaved, onToast }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => { setV(value ?? ''); }, [value]);
  const save = () => api.put('/api/v1/admin/teams/catalog-app-id', { id: v.trim() })
    .then(() => { onToast(v.trim() ? 'Teams 앱 ID를 저장했습니다' : 'Teams 앱 ID를 지웠습니다'); onSaved(); }).catch(e => onToast(e.message));
  return (
    <div data-teams-catalog style={{ padding: '0 14px 14px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
      <div style={{ width: 360 }}><Field label="Teams 앱 ID(조직 카탈로그)" value={v} onChange={e => setV(e.target.value)} placeholder="Teams 관리 센터 → 앱 관리 → 앱 → 앱 ID" /></div>
      <Button size="sm" variant="secondary" onClick={save}>저장</Button>
      <span style={{ color: 'var(--ink-3)' }}>{value ? '알림의 열기 = Teams 탭' : '없으면 알림의 열기 = 웹 링크'}</span>
    </div>
  );
}

function OutboxAdmin({ onToast }) {
  const [d, setD] = useState(null);
  const load = () => api.get('/api/v1/admin/outbox').then(setD).catch(e => onToast(e.message));
  useEffect(() => { load(); }, []);
  if (!d) return <Loading title="불러오는 중" />;
  const retry = id => api.post(`/api/v1/admin/outbox/${id}/retry`).then(() => { onToast('다시 보냅니다'); load(); }).catch(e => onToast(e.message));
  const sum = (kind, st) => d.counts.filter(c => c.kind === kind && c.status === st).reduce((a, c) => a + c.n, 0);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel>
        <PH title="채널 설정" sub="관리자 작업이 끝나야 실제로 나갑니다" right={<div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="secondary" onClick={() => { window.location.href = '/api/v1/admin/teams-package'; }}>Teams 앱 패키지 받기</Button>
          <Button size="sm" variant="secondary" onClick={load}>새로고침</Button></div>} />
        <div style={{ padding: 14, display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span>메일·Outlook 초대: {d.channels.mail ? <Pill tone="ok" dot>{d.channels.mailbox}</Pill> : <Pill tone="warn" dot>알림 사서함 미설정</Pill>}</span>
          <span>Teams Bot: {d.channels.teams ? <Pill tone="ok" dot>설정됨</Pill> : <Pill tone="warn" dot>미설정</Pill>}</span>
          <span>Teams 활동 피드: {d.channels.teamsFeed ? <Pill tone="ok" dot>설정됨</Pill> : <Pill tone="neutral" dot>자격 없음(건너뜀)</Pill>}</span>
        </div>
        <TeamsCatalogId value={d.teamsCatalogAppId} onSaved={load} onToast={onToast} />
        <div style={{ padding: '0 14px 14px', display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12 }}>
          {Object.keys(KIND).map(k => (
            <span key={k} className="mono" style={{ color: 'var(--ink-2)' }}>{KIND[k]} 보냄 {sum(k, 'sent')} · 실패 {sum(k, 'failed')} · 건너뜀 {sum(k, 'skipped')} · 대기 {sum(k, 'pending')}</span>))}
        </div>
      </Panel>
      <Panel>
        <PH title="Teams 봇이 받은 활동" sub="최근 24시간 — 종류·이름·결과별(내용은 남기지 않음)" />
        <div data-teams-bot-events style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          {(d.botEvents ?? []).length === 0 ? <span style={{ color: 'var(--ink-3)' }}>받은 활동이 없습니다</span> :
            d.botEvents.map((e, i) => <span key={i} className="mono">{e.type}{e.name ? ` ${e.name}` : ''} · {e.result} · {e.n}</span>)}
        </div>
      </Panel>
      <Panel scroll>
        <PH title="최근 발송" sub="최근 100건" />
        {d.recent.length === 0 ? <Empty icon="send" title="발송 기록이 없습니다" /> :
          <DataTable columns={[
            { key: 'created_at', header: '시각', mono: true, width: 130, render: o => T.dateTime(o.created_at).slice(5) },
            { key: 'kind', header: '채널', width: 100, render: o => KIND[o.kind] ?? o.kind },
            { key: 'title', header: '내용', render: o => <span style={{ fontSize: 12 }}>{o.title || '-'}{o.recipient ? ` → ${o.recipient}` : ''}</span> },
            { key: 'status', header: '상태', width: 90, render: o => { const [l, t] = OST[o.status] ?? [o.status, 'neutral']; return <Pill tone={t} dot>{l}</Pill>; } },
            { key: 'last_error', header: '사유', render: o => <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{o.last_error || ''}</span> },
            { key: 'act', header: '', width: 80, render: o => (o.status === 'failed' || o.status === 'skipped') && <Button size="sm" variant="secondary" onClick={() => retry(o.id)}>재발송</Button> },
          ]} rows={d.recent.map(o => ({ ...o, id: o.id }))} />}
      </Panel>
    </div>
  );
}

// ─── 서비스 등록 ─────────────────────────────────────────
const TLS_LEVEL = { ok: ['정상', 'ok'], warn: ['갱신 지연', 'warn'], critical: ['긴급', 'crit'] };
const SST = { provisioning: ['준비 중', 'info'], active: ['사용 중', 'ok'], error: ['오류', 'crit'], deleting: ['삭제 중', 'warn'] };
// 플랫폼 스토리지(설계 §10)
const BUCKET_KIND = {
  data: ['원본 데이터', '서비스가 쓰는 원본(첨부 등). 버전관리 — 덮어쓰거나 지운 것도 보관 일수 동안 되살릴 수 있음. 지난 버전 영구 삭제는 막힘'],
  backup: ['백업', '서비스 자체 백업. 객체 잠금 — 보관 일수 동안 아무도(관리 화면 포함) 지울 수 없고, 그 뒤 자동 삭제. 서비스 키에는 삭제 권한 없음'],
  dev: ['개발용', '개발 환경 전용(운영 데이터 금지). 개발용 키로만 접근하고 운영 버킷에는 권한 없음'],
};
const BST = { pending: ['대기', 'info'], ready: ['사용 중', 'ok'], error: ['오류', 'crit'], released: ['관리 해제', 'neutral'] };
const USE_LEVEL = { ok: null, warn: ['80% 넘음', 'warn'], full: ['상한 도달', 'crit'] };
const fmtBytes = n => {
  if (n == null) return '—';
  const v = Number(n);
  if (v < 1048576) return `${(v / 1024).toFixed(v < 10240 ? 1 : 0)} KiB`;
  if (v < 1073741824) return `${(v / 1048576).toFixed(v < 10485760 ? 1 : 0)} MiB`;
  return `${(v / 1073741824).toFixed(v < 10737418240 ? 2 : 0)} GiB`;
};
const STEP_LABEL = { entra_app: 'Entra 앱', workspace_api_preauth: 'Workspace API 연결', namespace: '배포 공간', secret_sso: '인증 값', database: 'DB',
  gitlab_project: 'GitLab 저장소', registry_pull: '이미지 받기', agent_token: '에이전트 토큰', agent_access: '에이전트 접근', argocd_app: '자동 배포', external_upstream: '원 서버 연결', ingress: '입구·로그인',
  egress: '나가는 연결 제한', guides: '가이드' };
// 단계 이름 뒤의 ':<버전·해시>'는 같은 단계의 갱신이다 — 화면에는 한 번만 보인다
const stepsText = steps => [...new Set(Object.keys(steps || {}).map(k => STEP_LABEL[k.split(':')[0]] ?? k.split(':')[0]))].join(' · ');

// ─── 감사(계획 문서 5.4 4a) ─────────────────────────────
/** 감사로그 — 조건(기간 필수·92일까지)은 서버가 검사한다. 감사 행은 읽기만(지우거나 고치지 않는다) */
function AuditAdmin({ onToast }) {
  const today = T.todayYmd();
  const [f, setF] = useState({ from: T.addDays(today, -6), to: today, actorId: '', resourceType: '', action: '' });
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const query = p => new URLSearchParams(Object.entries({ ...f, page: String(p) }).filter(([, v]) => v !== '')).toString();
  const load = p => api.get('/api/v1/admin/audit?' + query(p)).then(r => { setD(r); setPage(p); }).catch(e => onToast(e.message));
  useEffect(() => { load(1); }, []);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  const pages = d ? Math.max(1, Math.ceil(d.total / d.pageSize)) : 1;
  return (
    <Panel scroll>
      <PH title="감사 기록" sub={d ? `${d.total}건 · 최신 순` : ''} right={<div style={{ display: 'flex', gap: 6 }}>
        <Button size="sm" variant="secondary" onClick={() => { window.location.href = '/api/v1/admin/audit.csv?' + query(1); }} data-audit-csv>CSV 내보내기</Button>
      </div>} />
      <div data-audit-filters style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'end' }}>
        <Field label="시작" type="date" value={f.from} onChange={set('from')} />
        <Field label="끝" type="date" value={f.to} onChange={set('to')} />
        <Sel label="행위자" value={f.actorId} onChange={v => setF(x => ({ ...x, actorId: v ?? '' }))} placeholder="— 모두 —"
          options={PEOPLE.map(p => ({ v: p.id, l: `${p.name}${p.dept ? ' · ' + p.dept : ''}` }))} />
        <Field label="대상 종류" value={f.resourceType} onChange={set('resourceType')} placeholder="예: approval_document" />
        <Field label="행위(앞부분)" value={f.action} onChange={set('action')} placeholder="예: approval." />
        <Button size="sm" onClick={() => load(1)}>조회</Button>
      </div>
      {!d ? <Loading title="불러오는 중" /> : d.entries.length === 0 ? <Empty icon="shield" title="조건에 맞는 기록이 없습니다" /> : <>
        <DataTable columns={[
          { key: 'at', header: '시각', width: 140, render: e => <span className="mono" style={{ fontSize: 11.5 }}>{T.dateTime(e.created_at)}</span> },
          { key: 'actor', header: '행위자', width: 130, render: e => e.actor_name ?? <span style={{ color: 'var(--ink-3)' }}>시스템</span> },
          { key: 'action', header: '행위', render: e => <button className="link" data-audit-row={e.id} onClick={() => setOpen(open === e.id ? null : e.id)}><span className="mono" style={{ fontSize: 11.5 }}>{e.action}</span></button> },
          { key: 'res', header: '대상', render: e => <span className="mono" style={{ fontSize: 11 }}>{e.resource_type}{e.resource_id ? ' · ' + e.resource_id.slice(0, 8) : ''}</span> },
          { key: 'ip', header: 'IP', width: 120, render: e => <span className="mono" style={{ fontSize: 11 }} title={e.user_agent ?? ''}>{e.ip ?? '—'}</span> },
        ]} rows={d.entries.map(e => ({ ...e, id: e.id }))} />
        {open && (() => { const e = d.entries.find(x => x.id === open); return e && (
          <div data-audit-detail style={{ padding: 12, borderTop: '1px solid var(--line)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[['이전', e.before_json], ['이후', e.after_json]].map(([l, v]) => (
              <div key={l}><div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 4 }}>{l}</div>
                <pre className="mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, color: 'var(--ink-2)' }}>{v ? JSON.stringify(v, null, 2) : '—'}</pre></div>))}
            <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--ink-3)' }}>UA {e.user_agent ?? '—'} · 요청 {e.request_id ?? '—'}</div>
          </div>); })()}
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', padding: 10 }}>
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => load(page - 1)}>이전</Button>
          <span className="mono" style={{ fontSize: 11.5, alignSelf: 'center' }}>{page} / {pages}</span>
          <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => load(page + 1)}>다음</Button>
        </div>
      </>}
    </Panel>
  );
}

// ─── 운영 공지(계획 문서 5.3 3b) ─────────────────────────────
const OPS_STATE = { active: ['지금 보임', 'ok'], scheduled: ['예정', 'info'], ended: ['끝남', 'neutral'] };
/** datetime-local 값은 앱 시간대의 벽시계로 읽고 쓴다(브라우저 시간대가 아니다 — 계획 문서 5.3 3d) */
const localInput = iso => (iso ? `${T.ymd(iso)}T${T.hm(iso)}` : '');
const fromLocal = v => { if (!v) return null; const [day, t] = v.split('T'); const [h, m] = t.split(':').map(Number); return T.zonedIso(day, h + m / 60); };

function OpsAdmin({ onToast }) {
  const [list, setList] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(null);   // { id?, kind, title, body, serviceId, startsAt, endsAt } — 입력 값(datetime-local)
  const load = () => api.get('/api/v1/admin/ops-notices').then(r => setList(r.notices)).catch(e => onToast(e.message));
  useEffect(() => {
    load();
    api.get('/api/v1/admin/services').then(r => setServices(r.services)).catch(e => onToast('서비스 목록을 받지 못했습니다 — ' + e.message));
  }, []);
  if (!list) return <Loading title="불러오는 중" />;
  const save = async () => {
    const body = { kind: form.kind, title: form.title, body: form.body, serviceId: form.serviceId || null, startsAt: fromLocal(form.startsAt), endsAt: fromLocal(form.endsAt) };
    try {
      if (form.id) await api.patch(`/api/v1/admin/ops-notices/${form.id}`, body); else await api.post('/api/v1/admin/ops-notices', body);
      onToast(form.id ? '운영 공지를 고쳤습니다' : '운영 공지를 올렸습니다'); setForm(null); load();
    } catch (e) { onToast(e.message); }
  };
  const end = async n => {
    if (!window.confirm(`'${n.title}' 공지를 지금 끝낼까요?`)) return;
    try { await api.post(`/api/v1/admin/ops-notices/${n.id}/end`, {}); onToast('운영 공지를 끝냈습니다'); load(); } catch (e) { onToast(e.message); }
  };
  return (
    <Panel scroll>
      <PH title="운영 공지" sub="장애·점검·배포·안내 — 지금 보이는 공지는 홈 카드에, 장애·점검은 모든 화면 위에 표시됩니다(지우지 않고 끝냅니다)"
        right={<Button size="sm" onClick={() => setForm({ kind: 'maintenance', title: '', body: '', serviceId: '', startsAt: localInput(new Date().toISOString()), endsAt: '' })}>+ 운영 공지</Button>} />
      {list.length === 0 ? <Empty icon="megaphone" title="운영 공지가 없습니다" /> :
        <DataTable columns={[
          { key: 'kind', header: '종류', width: 70, render: n => <Pill tone={OPS_KIND[n.kind].tone} dot>{OPS_KIND[n.kind].label}</Pill> },
          { key: 'title', header: '제목', strong: true, render: n => <span data-ops-row={n.id}>{n.title}{n.service_name ? <span style={{ fontSize: 11, color: 'var(--ink-3)' }}> · {n.service_name}</span> : null}</span> },
          { key: 'period', header: '기간', width: 260, render: n => <span className="mono" style={{ fontSize: 11 }}>{opsPeriod(n)}{n.ended_at ? ` (끝냄 ${n.ended_by_name ?? ''})` : ''}</span> },
          { key: 'state', header: '상태', width: 90, render: n => <Pill tone={OPS_STATE[n.state][1]} dot data-ops-state={n.state}>{OPS_STATE[n.state][0]}</Pill> },
          { key: 'act', header: '', width: 120, render: n => <div style={{ display: 'flex', gap: 4 }}>
            {n.actions.edit && <Button size="sm" variant="secondary" onClick={() => setForm({ id: n.id, kind: n.kind, title: n.title, body: n.body, serviceId: n.service_id ?? '', startsAt: localInput(n.starts_at), endsAt: localInput(n.ends_at) })}>고치기</Button>}
            {n.actions.end && <Button size="sm" variant="secondary" onClick={() => end(n)}>끝내기</Button>}
          </div> },
        ]} rows={list} />}
      {form && (
        <Modal width={480} title={form.id ? '운영 공지 고치기' : '운영 공지'} icon="megaphone" onClose={() => setForm(null)} footer={<>
          <Button variant="secondary" size="sm" onClick={() => setForm(null)}>취소</Button>
          <Button size="sm" disabled={!form.title.trim() || !form.startsAt} onClick={save}>{form.id ? '저장' : '올리기'}</Button></>}>
          <div data-ops-form style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Sel label="종류" value={form.kind} onChange={v => setForm({ ...form, kind: v })} options={Object.entries(OPS_KIND).map(([v, k]) => ({ v, l: k.label }))} />
            <Field label="제목" value={form.title} maxLength={100} onChange={e => setForm({ ...form, title: e.target.value })} />
            <Field label="내용" as="textarea" value={form.body} maxLength={2000} onChange={e => setForm({ ...form, body: e.target.value })} />
            <Sel label="관련 서비스" value={form.serviceId} onChange={v => setForm({ ...form, serviceId: v ?? '' })} placeholder="— 없음 —"
              options={services.map(x => ({ v: x.id, l: x.name }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="시작" type="datetime-local" value={form.startsAt} onChange={e => setForm({ ...form, startsAt: e.target.value })} />
              <Field label="끝(비우면 끝낼 때까지)" type="datetime-local" value={form.endsAt} onChange={e => setForm({ ...form, endsAt: e.target.value })} />
            </div>
          </div>
        </Modal>
      )}
    </Panel>
  );
}

function ServiceAdmin({ onToast }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [cred, setCred] = useState(null);
  const [tls, setTls] = useState(null);
  const [store, setStore] = useState(null);
  const [calls, setCalls] = useState(null);
  const [backup, setBackup] = useState(null);
  const [info, setInfo] = useState(null);   // 서비스 정보 고치기
  const load = () => {
    api.get('/api/v1/admin/services').then(setD).catch(e => onToast(e.message));
    api.get('/api/v1/admin/tls').then(r => setTls(r.certificates)).catch(e => onToast(e.message));
    api.get('/api/v1/admin/backup').then(setBackup).catch(e => onToast(e.message));
  };
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  if (!d) return <Loading title="불러오는 중" />;
  const act = (fn, msg) => fn().then(() => { onToast(msg); load(); }).catch(e => onToast(e.message));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Panel>
        <PH title="등록된 서비스" sub={`${d.services.length}개 · 등록하면 로그인 연동·배포 공간·DB·저장소가 자동으로 준비됩니다`}
          right={<Button size="sm" onClick={() => setForm({ kind: 'cluster', ingress: true, slug: '', name: '', host: '', upstream: '', description: '', icon: 'box' })}>+ 서비스 등록</Button>} />
        {d.services.length === 0 ? <Empty icon="box" title="등록된 서비스가 없습니다" sub="실제 서비스를 붙일 때 등록하세요" /> :
          <DataTable columns={[
            { key: 'name', header: '서비스', strong: true, render: s => <span>{s.name} <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.slug}</span></span> },
            { key: 'host', header: '주소', render: s => s.host ? <span className="mono" style={{ fontSize: 11.5 }}>{s.host}</span>
              : <span style={{ fontSize: 11, color: 'var(--ink-3)' }} title="사람용 입구가 없는 내부 서비스 — 등록한 호출자만 부른다">입구 없음</span> },
            { key: 'kind', header: '형태', width: 90, render: s => SVC_KIND[s.kind] ?? s.kind },
            { key: 'status', header: '상태', width: 90, render: s => { const [l, t] = SST[s.status] ?? [s.status, 'neutral']; return <Pill tone={t} dot>{l}</Pill>; } },
            { key: 'secret', header: '비밀키 만료', width: 110, render: s => {
              if (!s.entra_secret_expires_at) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>;
              // 만료 30일 전에 자동 교체된다 — 이 아래로 남았거나 클러스터 반영이 안 끝났으면 문제가 있는 것
              const left = Math.floor((new Date(s.entra_secret_expires_at) - Date.now()) / 86400000);
              const bad = left < 30 || s.entra_secret_applied === false;
              return <span className="mono" title={s.entra_secret_rotated_at ? `마지막 자동 교체 ${T.dateTime(s.entra_secret_rotated_at)}` : '자동 교체 이력 없음'}
                style={{ fontSize: 11.5, color: bad ? 'var(--crit)' : 'var(--ink-2)' }}>{T.ymd(s.entra_secret_expires_at)}{s.entra_secret_applied === false ? ' (반영 대기)' : ''}</span>;
            } },
            { key: 'storage', header: '저장소', width: 120, render: s => {
              const bs = s.buckets || [];
              if (!bs.length) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>—</span>;
              const bad = bs.some(b => b.state === 'error' || b.usage_level === 'full'), warn = bs.some(b => b.state === 'pending' || b.usage_level === 'warn');
              const used = bs.reduce((a, b) => a + Number(b.usage_bytes || 0), 0);
              return <span style={{ fontSize: 11.5, color: bad ? 'var(--crit)' : warn ? 'var(--warn)' : 'var(--ink-2)' }}>{bs.length}개 · {fmtBytes(used)}</span>;
            } },
            { key: 'isolation', header: '입구·격리', width: 100, render: s => {
              // 프로비저너가 1분마다 대조 — 플랫폼 입구·격리 자원이 바뀌면 되돌리고, 플랫폼이 만들지 않은 경로는 남은 문제로 알린다
              const v = s.isolation;
              if (!v) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.status === 'active' ? '확인 전' : '—'}</span>;
              if (v.open.length) return <span title={v.open.join('\n')}><Pill tone="crit" dot>문제 {v.open.length}건</Pill></span>;
              if (v.failingSince) return <span title={`${T.dateTime(v.failingSince)}부터 실패: ${v.lastError || ''}`}><Pill tone="warn" dot>점검 실패</Pill></span>;
              return <span title={v.checkedAt ? `마지막 확인 ${T.dateTime(v.checkedAt)}` : ''}><Pill tone="ok" dot>정상</Pill></span>;
            } },
            { key: 'health', header: '상태', width: 90, render: s => {
              // 프로비저너 health-watch(1분) — 클러스터 서비스는 Deployment·StatefulSet 준비 수, 외부 서버는 점검 안 함
              if (!s.health) return <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{s.status === 'active' ? '확인 전' : '—'}</span>;
              const [l, t] = SVC_HEALTH[s.health];
              const why = s.health_detail?.reason === 'external' ? '외부 서버 — 점검 안 함' : s.health_detail?.error
                ?? (s.health_detail?.workloads ?? []).map(w => `${w.kind} ${w.name} ${w.ready}/${w.desired}`).join('\n');
              return <span data-svc-health={s.health} title={`${why}\n마지막 확인 ${s.health_checked_at ? T.dateTime(s.health_checked_at) : ''}`}><Pill tone={t} dot>{l}</Pill></span>;
            } },
            { key: 'steps', header: '진행', render: s => <span style={{ fontSize: 11, color: s.last_error ? 'var(--crit)' : 'var(--ink-3)' }}>{s.last_error || stepsText(s.provision_steps) || '대기'}</span> },
            { key: 'act', header: '', width: 250, render: s => (
              <div style={{ display: 'flex', gap: 5 }}>
                <Button size="sm" variant="secondary" onClick={() => setInfo(s)}>정보</Button>
                {s.actions.storage && <Button size="sm" variant="secondary" onClick={() => setStore(s)}>저장소</Button>}
                {s.kind === 'cluster' && s.status === 'active' && <Button size="sm" variant="secondary" onClick={() => setCalls(s)}>호출</Button>}
                {s.actions.credentials && <Button size="sm" variant="secondary" onClick={() => api.get(`/api/v1/admin/services/${s.id}/credentials`).then(c => setCred({ s, c })).catch(e => onToast(e.message))}>연동 정보</Button>}
                {s.actions.retry && <Button size="sm" variant="secondary" onClick={() => act(() => api.post(`/api/v1/admin/services/${s.id}/retry`), '다시 시도합니다')}>재시도</Button>}
                {s.actions.delete && <Button size="sm" variant="secondary" onClick={() => {
                  if (window.confirm(`'${s.name}' 서비스를 내릴까요? 로그인 연동과 배포 공간이 제거됩니다(DB·저장소는 보존).`)) act(() => api.post(`/api/v1/admin/services/${s.id}/delete`), '삭제를 시작했습니다'); }}>삭제</Button>}
              </div>) },
          ]} rows={d.services.map(s => ({ ...s, id: s.id }))} footer={<span>사용 권한은 권한 탭의 '서비스명 사용자/관리자' 역할로 줍니다</span>} />}
      </Panel>
      <Panel>
        <PH title="인증서" sub="입구와 바깥 TLS 주소(공용 검색 ES 등)가 실제로 내보내는 인증서를 1시간마다 확인합니다 · 21일 미만이면 갱신이 안 되고 있는 것(관리자 알림)" />
        {!tls ? <Loading title="불러오는 중" /> : tls.length === 0 ? <Empty icon="shield" title="아직 확인 기록이 없습니다" sub="프로비저너가 다음 실행에서 확인합니다" /> :
          <DataTable columns={[
            { key: 'host', header: '주소', strong: true, render: c => <span className="mono" style={{ fontSize: 11.5 }}>{c.host}{c.port ? <span style={{ color: 'var(--ink-3)' }}>:{c.port} · 바깥 주소</span> : null}</span> },
            { key: 'level', header: '상태', width: 90, render: c => { const [l, t] = TLS_LEVEL[c.alert_level] ?? [c.alert_level, 'neutral']; return <Pill tone={t} dot>{l}</Pill>; } },
            { key: 'not_after', header: '만료', width: 120, render: c => c.not_after
              ? <span className="mono" style={{ fontSize: 11.5 }}>{T.ymd(c.not_after)} <span style={{ color: 'var(--ink-3)' }}>({Math.floor((new Date(c.not_after) - Date.now()) / 86400000)}일)</span></span> : '—' },
            { key: 'issuer', header: '발급자', render: c => <span style={{ fontSize: 11.5, color: 'var(--ink-2)' }}>{c.issuer || '—'}</span> },
            { key: 'note', header: '확인', render: c => <span style={{ fontSize: 11, color: c.verify_error ? 'var(--crit)' : 'var(--ink-3)' }}>
              {c.verify_error || (c.checked_at ? T.dateTime(c.checked_at) : '')}</span> },
          ]} rows={tls.map(c => ({ ...c, id: c.host }))} />}
      </Panel>
      <BackupPanel b={backup} />
      {form && <ServiceForm init={form} suffix={d.domainSuffix} onClose={() => setForm(null)}
        onSave={body => api.post('/api/v1/admin/services', body).then(() => { onToast('등록했습니다. 준비가 끝나면 사용 중으로 바뀝니다'); setForm(null); load(); }).catch(e => onToast(e.message))} />}
      {cred && <CredModal data={cred} onClose={() => setCred(null)} onToast={onToast} />}
      {info && <ServiceInfoModal svc={info} onClose={() => setInfo(null)}
        onSave={body => api.patch(`/api/v1/admin/services/${info.id}`, body).then(() => { onToast('서비스 정보를 저장했습니다'); setInfo(null); load(); }).catch(e => onToast(e.message))} />}
      {store && <StorageModal svc={store} onClose={() => { setStore(null); load(); }} onToast={onToast} />}
      {calls && <ServiceCallsModal svc={calls} onClose={() => setCalls(null)} onToast={onToast} />}
    </div>
  );
}

// 서비스 형태 — platform은 이 저장소(코드)가 선언·배포하는 플랫폼 구성요소(운영 모니터링 등, 관리 화면에서 내리지 않는다)
const SVC_KIND = { cluster: '클러스터', external: '외부 서버', platform: '플랫폼' };
const SVC_HEALTH = { ok: ['정상', 'ok'], degraded: ['일부 장애', 'warn'], down: ['중단', 'crit'], unknown: ['모름', 'neutral'] };

/** 서비스 정보(설계 §13.4 운영자 포털 메타데이터) — 이름·설명·담당자·대상 사용자·로그 위치·알림 채널·runbook. 검사는 서버 */
function ServiceInfoModal({ svc, onClose, onSave }) {
  const [f, setF] = useState({ name: svc.name, description: svc.description ?? '', ownerUserId: svc.owner_user_id ?? '', audience: svc.audience ?? '',
    logLocation: svc.log_location ?? '', alertChannel: svc.alert_channel ?? '', runbookUrl: svc.runbook_url ?? '' });
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  return (
    <Modal width={500} title={`${svc.name} — 서비스 정보`} icon="box" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" disabled={!f.name.trim()} onClick={() => onSave({ ...f, ownerUserId: f.ownerUserId || null })}>저장</Button></>}>
      <div data-service-info={svc.id} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="이름" value={f.name} maxLength={60} onChange={set('name')} />
        <Field label="설명" value={f.description} maxLength={300} onChange={set('description')} />
        <Sel label="담당자" value={f.ownerUserId} onChange={v => setF(x => ({ ...x, ownerUserId: v ?? '' }))} placeholder="— 없음 —"
          options={PEOPLE.map(p => ({ v: p.id, l: `${p.name}${p.dept ? ' · ' + p.dept : ''}` }))} />
        <Field label="대상 사용자" value={f.audience} maxLength={200} onChange={set('audience')} placeholder="예: 재무팀·결재 관리자" />
        <Field label="로그 위치" value={f.logLocation} maxLength={300} onChange={set('logLocation')} placeholder="예: Grafana Loki {namespace=&quot;svc-expense&quot;}" />
        <Field label="알림 채널" value={f.alertChannel} maxLength={200} onChange={set('alertChannel')} placeholder="예: Teams 운영 채널" />
        <Field label="runbook" value={f.runbookUrl} maxLength={500} onChange={set('runbookUrl')} placeholder="https://" />
      </div>
    </Modal>
  );
}

function ServiceForm({ init, suffix, onClose, onSave }) {
  const [f, setF] = useState(init);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  // 사람용 입구(계획 문서 5.12 (1)) — 외부 서버는 입구가 유일한 길이라 늘 있다. 없으면 주소를 보내지 않는다(서버가 거절). 등록 뒤에는 바꾸지 않는다
  const ingress = f.kind === 'external' || f.ingress;
  const body = () => { const { host, ...rest } = f; return ingress ? { ...rest, host, ingress: true } : { ...rest, ingress: false }; };
  return (
    <Modal width={480} title="서비스 등록" icon="box" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" disabled={!f.slug || !f.name || (ingress && !f.host)} onClick={() => onSave(body())}>등록</Button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Sel label="형태" value={f.kind} onChange={v => setF(x => ({ ...x, kind: v || 'cluster' }))} options={[
          { v: 'cluster', l: '클러스터에 새로 올림 (GitLab·자동 배포·DB 준비)' }, { v: 'external', l: '이미 다른 서버에서 운영 중 (입구만 연결)' }]} />
        {f.kind === 'cluster' && <div data-service-ingress style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Sel label="사람용 입구" value={f.ingress ? 'yes' : 'no'} onChange={v => setF(x => ({ ...x, ingress: v !== 'no' }))} options={[
            { v: 'yes', l: '있음 — 사람이 브라우저로 여는 서비스 (주소·로그인 연동)' }, { v: 'no', l: '없음 — 다른 서비스만 부르는 내부 서비스 (예: 공용 검색)' }]} />
          {!f.ingress && <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>주소·인증서·로그인 연동·런처 항목을 만들지 않습니다. 이 서비스를 부를 서비스는 등록 뒤 '호출'에서 더합니다. 등록한 뒤에는 바꿀 수 없습니다.</span>}
        </div>}
        <Field label="식별자 (영문 소문자)" value={f.slug} onChange={e => { const v = e.target.value.toLowerCase(); setF(x => ({ ...x, slug: v, host: x.host && x.host !== `${x.slug}.${suffix}` ? x.host : (v ? `${v}.${suffix}` : '') })); }} placeholder="예: expense" />
        <Field label="이름" value={f.name} onChange={set('name')} placeholder="예: 경비정산" />
        {ingress && <Field label="주소" value={f.host} onChange={set('host')} hint="인증서·DNS·외부 공개 경로 등록이 따로 필요합니다(서비스 배포 가이드 참고)" />}
        {f.kind === 'external' && <Field label="원 서버 주소" value={f.upstream} onChange={set('upstream')} placeholder="http://192.168.20.x:8080" hint="이 서버는 클러스터에서 오는 접속만 받도록 방화벽을 설정해야 합니다" />}
        <Field label="설명" value={f.description} onChange={set('description')} />
      </div>
    </Modal>
  );
}

function CredModal({ data, onClose, onToast }) {
  const { s, c } = data;
  // 여러 줄인 SSH 키는 따로 보여준다(나머지는 KEY=값 한 줄씩 — .env에 그대로 붙일 수 있게)
  const text = Object.entries(c).filter(([k, v]) => v && k !== 'GIT_DEPLOY_KEY' && k !== 'STORAGE').map(([k, v]) => `${k}=${v}`).join('\n');
  const envText = o => Object.entries(o).map(([k, v]) => `${k}=${v}`).join('\n');
  const pre = { fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: 12, maxHeight: 220, overflow: 'auto' };
  return (
    <Modal width={620} title={`${s.name} — 연동 정보`} icon="key" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={() => { navigator.clipboard?.writeText(text).then(() => onToast('복사했습니다')); }}>복사</Button>
      <Button size="sm" onClick={onClose}>닫기</Button></>}>
      <div style={{ fontSize: 12, color: 'var(--crit)', marginBottom: 10 }}>비밀키가 포함돼 있습니다. 서비스 담당(에이전트)에게만 전달하세요. 열람 기록이 남습니다.</div>
      {s.kind === 'cluster' && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>클러스터 서비스는 같은 값이 서비스 공간의 Secret(workspace-sso, db)으로 이미 들어가 있습니다. 에이전트에게는 GitLab 주소와 토큰만 주면 됩니다.</div>}
      <pre className="mono" style={{ fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: 12, maxHeight: 300, overflow: 'auto' }}>{text}</pre>
      {c.GIT_DEPLOY_KEY && <>
        <div style={{ fontSize: 12, fontWeight: 600, margin: '12px 0 6px' }}>GIT_DEPLOY_KEY (코드 올리기용 SSH 키 — 파일로 저장, 권한 600)</div>
        <pre className="mono" style={{ fontSize: 10.5, whiteSpace: 'pre', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: 12, maxHeight: 160, overflow: 'auto' }}>{c.GIT_DEPLOY_KEY}</pre></>}
      {c.STORAGE && [['app', '저장소 — 서비스용 (외부 서버의 .env에)'], ['dev', '저장소 — 개발용 (개발 환경 .env에, 운영 버킷 권한 없음)']].filter(([k]) => c.STORAGE[k]).map(([k, label]) => (
        <div key={k}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '12px 0 6px' }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
            <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard?.writeText(envText(c.STORAGE[k])).then(() => onToast('복사했습니다')); }}>복사</Button>
          </div>
          <pre className="mono" style={pre}>{envText(c.STORAGE[k])}</pre>
        </div>))}
      {s.kind === 'cluster' && (s.buckets || []).length > 0 && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 10 }}>서비스용 저장소 값은 서비스 공간의 Secret(workspace-storage)에 들어가 있습니다 — deploy/에서 envFrom으로 읽게 하면 됩니다.</div>}
    </Modal>
  );
}

const BK_LEVEL = { unconfigured: ['설치 전', 'neutral'], ok: ['정상', 'ok'], critical: ['이상', 'crit'] };
/** DB 백업(설계 §11): DB 서버의 기준 백업 기록 + WAL 보관 현재값. 판정은 프로비저너가 1시간마다 */
function BackupPanel({ b }) {
  const st = b?.status, a = b?.archiver;
  const [l, t] = BK_LEVEL[st?.level] ?? ['확인 전', 'neutral'];
  return (
    <Panel>
      <PH title="DB 백업" sub="DB 서버가 매일 02:15 기준 백업(일요일 전체)과 WAL 연속 보관으로 MinIO(다른 VM)에 백업합니다 · 1시간마다 확인, 실패·지연 시 관리자 알림"
        right={<Pill tone={t} dot>{l}</Pill>} />
      {!b ? <Loading title="불러오는 중" /> : <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '0 2px' }}>
        {(st?.reasons || []).length > 0 && <div style={{ fontSize: 12, color: st.level === 'critical' ? 'var(--crit)' : 'var(--ink-3)' }}>{st.reasons.join(' · ')}</div>}
        {a && <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
          WAL 보관 {a.archive_mode === 'on' ? '켜짐' : '꺼짐'}
          {a.archive_mode === 'on' && <> · 마지막 보관 {a.last_archived_time ? T.dateTime(a.last_archived_time) : '없음'} · 누적 {a.archived_count}건
            {Number(a.failed_count) > 0 && <span style={{ color: 'var(--warn)' }}> · 실패 {a.failed_count}건(마지막 {a.last_failed_time ? T.dateTime(a.last_failed_time) : '-'})</span>}</>}
        </div>}
        {(b.runs || []).length === 0 ? <Empty icon="database" title="백업 기록이 없습니다" sub="DB 서버에 백업을 설치하면 여기에 쌓입니다(ops/backup)" /> :
          <DataTable columns={[
            { key: 'kind', header: '종류', width: 90, render: r => ({ 'db-full': '전체', 'db-diff': '차등', 'db-restore-drill': '복구 시험' })[r.kind] ?? r.kind },
            { key: 'at', header: '끝난 시각', width: 170, render: r => <span className="mono" style={{ fontSize: 11.5 }}>{T.dateTime(r.finished_at)}</span> },
            { key: 'ok', header: '결과', width: 80, render: r => <Pill tone={r.ok ? 'ok' : 'crit'} dot>{r.ok ? '성공' : '실패'}</Pill> },
            { key: 'size', header: '크기', width: 100, render: r => r.detail?.last?.size ? fmtBytes(r.detail.last.size) : '—' },
            { key: 'lock', header: '삭제 잠금', width: 130, render: r => { const l = r.detail?.lock; if (!l) return '—';
              return l.enabled ? <span style={{ fontSize: 11.5 }}>{l.mode} {l.days ?? (l.years ? `${l.years}년` : '')}{l.days ? '일' : ''}</span> : <span style={{ fontSize: 11.5, color: 'var(--crit)' }}>확인 안 됨</span>; } },
            { key: 'err', header: '', render: r => <span style={{ fontSize: 11, color: 'var(--crit)' }}>{r.error ? String(r.error).slice(0, 160) : ''}</span> },
          ]} rows={b.runs.map((r, i) => ({ ...r, id: i }))} />}
      </div>}
    </Panel>
  );
}

function StorageModal({ svc, onClose, onToast }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get(`/api/v1/admin/services/${svc.id}/storage`).then(setD).catch(e => onToast(e.message));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  const act = (fn, msg) => fn().then(() => { onToast(msg); setForm(null); load(); }).catch(e => onToast(e.message));
  const st = d?.status;
  const live = (d?.buckets || []).filter(b => b.state !== 'released');
  return (
    <Modal width={860} title={`${svc.name} — 저장소`} icon="database" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={() => setForm({ name: '', kind: 'data', retentionDays: 30, quotaGiB: '' })}>+ 버킷 추가</Button>
      <Button size="sm" onClick={onClose}>닫기</Button></>}>
      {!d ? <Loading title="불러오는 중" /> : <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12, color: st?.configured ? 'var(--ink-3)' : 'var(--warn)' }}>
          {!st?.configured ? '플랫폼 저장소 자격이 아직 설정되지 않았습니다 — 등록한 버킷은 \'대기\'로 남고, 설정되면 자동으로 만들어집니다.'
            : <>MinIO <span className="mono">{st.endpoint}</span> · 반영은 1분마다{st.lastRunAt ? ` (마지막 ${T.hm(st.lastRunAt)})` : ''}
              {st.error && <span style={{ color: 'var(--crit)' }}> · 마지막 실행 오류: {st.error}</span>}</>}
        </div>
        {live.length === 0 ? <Empty icon="database" title="등록된 버킷이 없습니다" sub="서비스가 파일을 저장해야 하면 버킷을 추가하세요" /> :
          <DataTable columns={[
            { key: 'bucket', header: '버킷', strong: true, render: b => <span className="mono" style={{ fontSize: 11.5 }}>{b.bucket}</span> },
            { key: 'kind', header: '종류', width: 90, render: b => <span title={BUCKET_KIND[b.kind]?.[1]}>{BUCKET_KIND[b.kind]?.[0] ?? b.kind}</span> },
            { key: 'ret', header: '보관', width: 60, render: b => b.kind === 'dev' ? '—' : `${b.retention_days}일` },
            { key: 'use', header: '사용량 / 상한', width: 170, render: b => { const lv = USE_LEVEL[b.usage_level];
              return <span style={{ fontSize: 11.5 }}>{fmtBytes(b.usage_bytes)} / {b.quota_bytes ? fmtBytes(b.quota_bytes) : '상한 없음'} {lv && <Pill tone={lv[1]} dot={false}>{lv[0]}</Pill>}</span>; } },
            { key: 'state', header: '상태', width: 90, render: b => { const [l, t] = BST[b.state] ?? [b.state, 'neutral']; return <Pill tone={t} dot>{l}</Pill>; } },
            { key: 'err', header: '', render: b => <span style={{ fontSize: 11, color: 'var(--crit)' }}>{b.last_error || ''}</span> },
            { key: 'act', header: '', width: 140, render: b => (
              <div style={{ display: 'flex', gap: 5 }}>
                <Button size="sm" variant="secondary" onClick={() => setForm({ id: b.id, name: b.name, kind: b.kind, retentionDays: b.retention_days, quotaGiB: b.quota_bytes ? +(b.quota_bytes / 1073741824).toFixed(2) : '' })}>수정</Button>
                <Button size="sm" variant="secondary" onClick={() => {
                  if (window.confirm(`${b.bucket}을(를) 관리 해제할까요?\n서비스 키에서 이 버킷 권한이 빠집니다(키가 새로 발급되고 서비스가 재시작됨). 버킷과 데이터는 지우지 않습니다.`))
                    act(() => api.post(`/api/v1/admin/services/${svc.id}/buckets/${b.id}/release`), '관리 해제했습니다'); }}>관리 해제</Button>
              </div>) },
          ]} rows={live} footer={<span>사용량은 지난 버전을 포함해 1시간마다 잽니다 · 상한에 닿으면 쓰기가 거부됩니다</span>} />}
        {(d.keys || []).length > 0 && <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>서비스 키</div>
          <DataTable columns={[
            { key: 'purpose', header: '용도', width: 90, render: k => k.purpose === 'dev' ? '개발용' : '서비스용' },
            { key: 'id', header: '키', width: 90, render: k => <span className="mono" style={{ fontSize: 11.5 }}>{k.access_key}</span> },
            { key: 'exp', header: '만료', width: 110, render: k => <span className="mono" style={{ fontSize: 11.5 }}>{T.ymd(k.expires_at)}</span> },
            { key: 'state', header: '상태', render: k => k.state === 'current'
              ? <span style={{ fontSize: 11.5 }}>사용 중{k.purpose === 'app' && svc.kind === 'cluster' && !k.applied ? ' (서비스 반영 대기)' : ''}</span>
              : <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>교체됨 — {T.ymd(k.retire_after)} 삭제 예정</span> },
          ]} rows={d.keys.map((k, i) => ({ ...k, id: i }))} footer={<span>만료 30일 전에 자동 교체됩니다. 클러스터 서비스는 새 키가 Secret에 들어가고 재시작되며, 외부·개발용 키는 관리자 알림 후 '연동 정보'에서 옮겨 넣습니다</span>} />
        </div>}
      </div>}
      {form && <BucketForm init={form} slug={svc.slug} onClose={() => setForm(null)} onSave={body => form.id
        ? act(() => api.patch(`/api/v1/admin/services/${svc.id}/buckets/${form.id}`, { retentionDays: body.retentionDays, quotaGiB: body.quotaGiB === '' ? null : body.quotaGiB }), '저장했습니다. 1분 안에 반영됩니다')
        : act(() => api.post(`/api/v1/admin/services/${svc.id}/buckets`, { ...body, quotaGiB: body.quotaGiB === '' ? null : body.quotaGiB }), '등록했습니다. 1분 안에 만들어집니다')} />}
    </Modal>
  );
}

function BucketForm({ init, slug, onClose, onSave }) {
  const [f, setF] = useState(init);
  const edit = Boolean(init.id);
  const set = k => e => setF(x => ({ ...x, [k]: e.target.value }));
  return (
    <Modal width={480} title={edit ? '버킷 수정' : '버킷 추가'} icon="database" onClose={onClose} footer={<>
      <Button variant="secondary" size="sm" onClick={onClose}>취소</Button>
      <Button size="sm" disabled={!f.name} onClick={() => onSave({ name: f.name, kind: f.kind, retentionDays: Number(f.retentionDays), quotaGiB: f.quotaGiB })}>{edit ? '저장' : '추가'}</Button></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!edit && <Field label="이름" value={f.name} onChange={e => setF(x => ({ ...x, name: e.target.value.toLowerCase() }))} placeholder="예: files"
          hint={`버킷 이름은 fgcp-svc-${slug}-${f.name || '이름'} · 서비스에는 ${'S3_BUCKET_' + (f.name || '이름').toUpperCase().replace(/-/g, '_')}로 전달`} />}
        {!edit && <Sel label="종류 (만든 뒤 바꿀 수 없음)" value={f.kind} onChange={v => setF(x => ({ ...x, kind: v || 'data' }))}
          options={Object.entries(BUCKET_KIND).map(([v, [l]]) => ({ v, l }))} />}
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{BUCKET_KIND[f.kind]?.[1]}</div>
        {f.kind !== 'dev' && <Field label="보관 일수" value={f.retentionDays} onChange={set('retentionDays')}
          hint={f.kind === 'backup' ? '이 기간 동안 아무도 지울 수 없고 그 뒤 자동 삭제됩니다. 바꾸면 새로 올리는 백업부터 적용됩니다' : '덮어쓰거나 지운 이전 버전을 이 기간 동안 보관합니다'} />}
        <Field label="상한 (GiB, 비우면 없음)" value={f.quotaGiB} onChange={set('quotaGiB')} hint="지난 버전 포함. 80%·100%에서 관리자 알림, 100%면 쓰기 거부" />
      </div>
    </Modal>
  );
}

export { Admin };
